/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import styled from "styled-components";

import { Auth } from "../../../classes/user/auth/Auth";
import { StandardButton } from "src/components/UI/Buttons/StandardButton";

interface Props {
  error: Error;
  componentStack: any;
  resetError: () => void;
  message: string;
}

export const FullScreenError = ({ error, componentStack, resetError, message }: Props) => (
  <ErrorWrapper>
    <ErrorBody>
      <ErrorTextBox style={{ marginTop: "300px" }}>{message}</ErrorTextBox>
      <ErrorTextBox style={{ fontSize: "14px", marginTop: "10px" }}>
        An anonymous error has been sent to Vienna.
      </ErrorTextBox>
      <ErrorButtonWrapper onClick={() => resetError()} style={{ cursor: "pointer" }}>
        <StandardButton>Refresh</StandardButton>
      </ErrorButtonWrapper>
      {process.env.ENVIRONMENT !== "prod" && (
        <>
          <MinorErrorText style={{ marginTop: "50px" }} as="code">
            {error.toString()}
          </MinorErrorText>
          <MinorErrorText>{componentStack}</MinorErrorText>{" "}
        </>
      )}
    </ErrorBody>
  </ErrorWrapper>
);

export const SimpleError = ({ message = "LOADING FAULT" }: { message?: string }) => (
  <ErrorWrapper>
    <ErrorBody>
      <ErrorTextBox>{message}</ErrorTextBox>
      <div>
        <ErrorButtonWrapper onClick={() => location.reload()} style={{ cursor: "pointer" }}>
          <StandardButton styles={{ minWidth: "120px" }}>Refresh</StandardButton>
        </ErrorButtonWrapper>
        <ErrorButtonWrapper onClick={() => Auth.instance.logout()} style={{ cursor: "pointer" }}>
          <StandardButton styles={{ minWidth: "120px" }}>Logout</StandardButton>
        </ErrorButtonWrapper>
      </div>
    </ErrorBody>
  </ErrorWrapper>
);

export const ErrorBody = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

export const ErrorTextBox = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 14pt;
  color: ${({ theme }) => theme.text.default};
  padding: 8px;
  border: 2px solid ${({ theme }) => theme.text.default};
`;

const ErrorWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: flex-start;
  justify-content: center;
  overflow-y: scroll;
`;

const MinorErrorText = styled.pre`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10pt;
  color: ${({ theme }) => theme.text.default};
`;

const ErrorButtonWrapper = styled.div`
  margin: 15px;
`;
